import { AntDesign, Ionicons } from "@expo/vector-icons";
import { parse } from "date-fns";
import { router, useFocusEffect } from "expo-router";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text, View, FlatList, Pressable, TextInput } from "react-native";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";

import { Filters, FiltersOptions } from "@/components/management/Filters";
import { generateAllCustomersXlsx } from "@/utils/management/ExportAllCustomersData";
import { getDisplayedPages } from "@/utils/management/Pages";

interface CustomerProps {
  data: any;
}

export default function CustomersList(props: CustomerProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const { t } = useTranslation();
  const customersViewWidth = useSharedValue(100);
  const pageLimit = 15;

  useEffect(() => {
    customersViewWidth.value = withTiming(showFilters ? 80 : 100, {
      duration: 500,
    });
  }, [showFilters]);

  const animatedStyle = useAnimatedStyle(() => ({
    width: `${customersViewWidth.value}%`,
  }));

  useFocusEffect(
    React.useCallback(() => {
      return () => {
        setCurrentPage(1);
        setSearchQuery("");
      };
    }, [])
  );

  const checkStatus = (status: string) => {
    if (status === "ACTIVE") {
      return "text-green";
    } else {
      return "text-black1";
    }
  };

  const renderPageIndicator = ({ item }: { item: number }) => (
    <Pressable onPress={() => setCurrentPage(item)} className="mx-1">
      <View
        className={`w-8 h-8 rounded-full flex items-center justify-center ${
          currentPage === item
            ? "bg-purple1 hover:bg-purple3"
            : "bg-gray hover:bg-lightgray"
        }`}
      >
        <Text
          className={`${currentPage === item ? "text-white" : "text-black1"}`}
        >
          {item}
        </Text>
      </View>
    </Pressable>
  );

  //Apply filter based on filter options object values
  const handleFilter = (filter: FiltersOptions) => {
    let customersData = [...props.data];

    if (filter.query && filter.query !== "") {
      const query = filter.query;
      customersData = customersData.filter(
        (customer) =>
          customer.application?.customerDetailsCorporation?.registeredName
            ?.toLowerCase()
            .includes(query?.toLowerCase()) ||
          customer.application?.customerDetailsIndividual?.firstName
            ?.toLowerCase()
            .includes(query?.toLowerCase()) ||
          customer.id?.toLowerCase().includes(query?.toLowerCase())
      );
    }

    if (filter.status && filter.status !== "") {
      customersData = customersData.filter(
        (customer) => customer.status === filter.status
      );
    }

    if (filter.type && filter.type !== "") {
      customersData = customersData.filter(
        (customer) => customer.customerType === filter.type
      );
    }

    if (filter.date && filter.date !== "" && filter.date !== " - ") {
      const dateSplit = filter.date.split(" - ");

      const startDate = new Date(dateSplit[0].split("/").reverse().join("-"));
      const endDate = dateSplit[1]
        ? new Date(dateSplit[1].split("/").reverse().join("-"))
        : new Date();

      customersData = customersData.filter((customer) => {
        const customerDate = new Date(customer.application?.createdAt);
        return customerDate >= startDate && customerDate <= endDate
          ? customer
          : null;
      });
    }
    return customersData;
  };

  const paginatedCustomers =
    filteredCustomers.length > 0
      ? filteredCustomers.slice(
          (currentPage - 1) * pageLimit,
          currentPage * pageLimit
        )
      : props.data.slice(
          (currentPage - 1) * pageLimit,
          currentPage * pageLimit
        );

  const totalPages = Math.ceil(
    (filteredCustomers.length > 0 ? filteredCustomers : props.data).length /
      pageLimit
  );

  return (
    <Animated.View className="w-full p-2">
      <View className="w-full flex-row justify-between my-4">
        <View>
          <View className="absolute left-4 top-3">
            <AntDesign name="search1" size={16} color="#BDBDBD" />
          </View>
          <TextInput
            className="border border-midgray rounded-full pl-10 py-2 text-base"
            placeholder={t("common:terms.searchcustomer")}
            placeholderTextColor="#BDBDBD"
            value={searchQuery}
            onChangeText={(text) => {
              setSearchQuery(text);
              const filteredList = handleFilter({ query: text });
              setFilteredCustomers(filteredList);
            }}
          />
        </View>
        <View className="flex-row items-center gap-6">
          {filteredCustomers.length > 0 && (
            <Pressable
              onPress={() => setFilteredCustomers([])}
              className="flex-col lg:flex-row gap-x-2 items-center justify-center"
            >
              <AntDesign name="close" size={18} color="#BDBDBD" />
              <Text className="text-midgray text-xs lg:text-sm hover:text-darkergray">
                {t("common:terms.clearfilters")}
              </Text>
            </Pressable>
          )}

          <Pressable
            onPress={() => setShowFilters(!showFilters)}
            className="flex-col lg:flex-row gap-x-2 items-center justify-center"
          >
            <Ionicons name="filter" size={18} color="#BDBDBD" />
            <Text className="text-midgray text-xs lg:text-sm hover:text-darkergray">
              {t("common:terms.filters")}
            </Text>
          </Pressable>
          <Pressable
            onPress={() => generateAllCustomersXlsx(props.data)}
            className="flex-col lg:flex-row gap-x-2 items-center justify-center"
          >
            <AntDesign name="download" size={18} color="#BDBDBD" />
            <Text className="text-midgray text-xs lg:text-sm hover:text-darkergray">
              {t("common:terms.exportcustomers")}
            </Text>
          </Pressable>
        </View>
      </View>
      <Animated.View className="w-full flex-row gap-2">
        <Animated.View className="flex-col" style={animatedStyle}>
          <View className="py-2 px-4 w-full flex-row">
            <View className="w-1/4 items-start">
              <Text className="font-thin text-lg text-darkergray">
                {t("management/home:board.id")}
              </Text>
            </View>
            <View className="w-1/4 items-start">
              <Text className="font-thin text-lg text-darkergray">
                {t("management/home:board.name")}
              </Text>
            </View>
            <View className="w-1/4 items-start">
              <Text className="font-thin text-lg text-darkergray">
                {t("management/home:board.status")}
              </Text>
            </View>
            <View className="w-1/4 items-start">
              <Text className="font-thin text-lg text-darkergray">
                {t("management/home:board.type")}
              </Text>
            </View>
          </View>
          <FlatList
            data={paginatedCustomers}
            renderItem={({ item }: any) => (
              <Pressable
                className="transition-colors delay-0 bg-gray hover:bg-lightgray border border-lightgray flex-row w-full py-2 px-4 rounded-lg my-1.5"
                onPress={() =>
                  router.push(
                    `/management/${item.id}_${item.customerType.toLowerCase()}`
                  )
                }
              >
                <View className="w-1/4 items-start">
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    className="font-thin text-sm text-black max-w-48"
                  >
                    {item.id}
                  </Text>
                </View>
                <View className="w-1/4 items-start">
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    className="font-thin text-sm text-black"
                  >
                    {item.application?.customerDetailsCorporation
                      ?.registeredName
                      ? item.application?.customerDetailsCorporation
                          ?.registeredName
                      : `${item.application?.customerDetailsIndividual?.firstName} ${item.application?.customerDetailsIndividual?.lastName}`}
                  </Text>
                </View>
                <View className="w-1/4 items-start">
                  <Text
                    className={`font-thin text-sm ${checkStatus(item.status)}`}
                  >
                    {item.status}
                  </Text>
                </View>
                <View className="w-1/4 items-start">
                  <Text className="font-thin text-sm">
                    {item.application?.customerDetailsCorporation
                      ?.registeredName
                      ? t("management/home:applicationtype.corporation")
                      : t("management/home:applicationtype.individual")}
                  </Text>
                </View>
              </Pressable>
            )}
          />
          <FlatList
            horizontal
            data={getDisplayedPages(totalPages, currentPage)}
            renderItem={renderPageIndicator}
            contentContainerClassName="mt-4 w-full items-center justify-center"
          />
        </Animated.View>
        {showFilters && (
          <Animated.View className="w-1/5">
            <Filters
              onClose={() => {
                setFilteredCustomers([]);
                setShowFilters(false);
              }}
              setFilters={(filters) => {
                const filteredList = handleFilter(filters);
                setFilteredCustomers(filteredList);
                setShowFilters(false);
              }}
            />
          </Animated.View>
        )}
      </Animated.View>
    </Animated.View>
  );
}
